'use client'

import { useState } from 'react'
import { useClerk, useUser } from '@clerk/nextjs'
import { Calendar, KeyRound, LogOut, Mail, Pencil } from 'lucide-react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ChangePasswordDialog } from '@/features/auth'
import { useMyInvestorCases, useMyInvestorProfile } from '@/features/investors'
import { InvestorShell } from '@/features/investor/components/investor-shell'
import { EditProfileDialog } from '@/features/investor/components/profile/edit-profile-dialog'
import { NotificationsCard } from '@/features/investor/components/profile/notifications-card'
import { ServicesListCard } from '@/features/investor/components/profile/services-list-card'

function initials(name: string | null | undefined): string {
  if (!name) return 'I'
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('')
}

export function InvestorProfileView() {
  const { user } = useUser()
  const { signOut } = useClerk()
  const { data: investor } = useMyInvestorProfile()
  const { data: investorCases = [] } = useMyInvestorCases()
  const [editOpen, setEditOpen] = useState(false)
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)

  const name = user?.fullName ?? investor?.name ?? 'Investor'
  const email = user?.primaryEmailAddress?.emailAddress ?? investor?.email ?? ''

  return (
    <InvestorShell active="profile" title="Profile" description="Your account and preferences.">
      <Card>
        <CardContent className="flex flex-wrap items-center gap-x-8 gap-y-4">
          <div className="flex min-w-0 items-center gap-3.5">
            <Avatar className="size-13">
              <AvatarFallback className="bg-primary text-primary-foreground text-lg font-semibold">
                {initials(name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="text-base font-semibold">{name}</p>
              <p className="text-muted-foreground text-sm">Nivex account</p>
            </div>
          </div>

          {email && (
            <div className="flex items-center gap-2.5">
              <span className="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-lg">
                <Mail className="size-4" />
              </span>
              <div className="min-w-0">
                <p className="text-muted-foreground text-xs">Email</p>
                <p className="truncate text-sm font-medium">{email}</p>
              </div>
            </div>
          )}

          {investor?.createdAt && (
            <div className="flex items-center gap-2.5">
              <span className="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-lg">
                <Calendar className="size-4" />
              </span>
              <div className="min-w-0">
                <p className="text-muted-foreground text-xs">Member since</p>
                <p className="text-sm font-medium">
                  {new Date(investor.createdAt).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </p>
              </div>
            </div>
          )}

          <div className="ml-auto flex shrink-0 gap-2">
            {investor && (
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                <Pencil className="size-3.5" /> Edit profile
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setChangePasswordOpen(true)}>
              <KeyRound className="size-3.5" /> Change Password
            </Button>
          </div>
        </CardContent>
      </Card>

      {investor && (
        <EditProfileDialog open={editOpen} onOpenChange={setEditOpen} profile={investor} />
      )}
      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />

      <ServicesListCard cases={investorCases} />

      {investor && <NotificationsCard />}

      <div>
        <Button
          variant="outline"
          className="border-destructive/30 bg-destructive/5 text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={() => void signOut({ redirectUrl: '/login' })}
        >
          <LogOut className="size-4" strokeWidth={2} /> Sign out
        </Button>
      </div>
    </InvestorShell>
  )
}
